import React, { useState, useMemo, useCallback } from 'react';
import { CSVLink } from 'react-csv';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { MinusCircle, Star } from 'lucide-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import HeatmapSettingsPanel from '../../Screener/HeatmapSettingsPanel';
import {
  SPREAD_SCENARIO_OPTIONS,
  DEFAULT_SPREAD_SCENARIOS,
  computeSpreadHeatmapRow,
  findSpreadBreakeven,
  getSpreadCellColor,
  formatSpreadCell,
  formatScenarioLabel,
  formatCurrency,
  getUserSpreadScenarios,
  saveUserSpreadScenarios,
  clearUserSpreadScenarios,
} from '../../../utils/spreadCalculations';
import { formatStrikePair } from '../../../utils/spreadMappings';

/**
 * Columns shown before the scenario grid. The first five are sticky so the
 * contract stays identifiable while scrolling the scenario columns, matching
 * the screener heatmap's layout.
 */
const ESSENTIAL_COLUMNS = [
  { key: 'Ticker', label: 'Ticker', width: '65px', stickyIndex: 1 },
  { key: 'Spread Type', label: 'Spread', width: '95px', stickyIndex: 2 },
  { key: 'Strikes', label: 'Strikes', width: '110px', stickyIndex: 3 },
  { key: 'Expiration', label: 'Expiration', width: '90px', stickyIndex: 4 },
  { key: 'Net Entry', label: 'Net\nEntry', width: '75px', stickyIndex: 5 },
  { key: 'Max Profit', label: 'Max\nProfit', width: '80px' },
  { key: 'Max Loss', label: 'Max\nLoss', width: '80px' },
  { key: 'Breakeven', label: 'Breakeven', width: '85px' },
  { key: 'Underlying Price', label: 'Underlying', width: '90px' },
  { key: 'DaysToExit', label: 'Days to\nExit', width: '60px' },
];

const HEADER_TOOLTIPS = {
  'Spread Type': 'The strategy: debit spreads are paid for up front, credit spreads collect premium.',
  Strikes: 'Long strike / short strike. Both legs share the same expiration.',
  'Net Entry': 'What the spread costs (debit) or pays you (credit), per share.',
  'Max Profit': 'The most this spread can make, per contract. Verticals cap both sides.',
  'Max Loss': 'The most this spread can lose, per contract — your capital at risk.',
  Breakeven: 'Underlying price at which the position breaks even at expiration.',
  'Days to Exit': 'Simulated holding period from today — not the expiration date.',
};

const rowKey = (row) => [
  row['Ticker'], row['Spread Key'], row['Long Strike'],
  row['Short Strike'], row['Expiration'],
].join('|');

/** Stable per-column tour anchor, e.g. "Net Entry" -> "net-entry". */
const tourColKey = (colKey) => (
  colKey === 'DaysToExit' ? 'days-to-exit' : colKey.toLowerCase().replace(/\s+/g, '-')
);

/**
 * Elasticity heatmap for vertical spreads.
 *
 * Rows are spreads, columns are underlying % moves, cells are the projected
 * return. Unlike the single-option heatmap the default metric is Return on
 * Risk — a spread's payoff is capped, so "% of the capital you put at risk" is
 * the comparable number across debits and credits alike.
 */
function SpreadHeatmapView({
  results,
  favoritedRows, setFavoritedRows,
  removedRows, setRemovedRows,
  showFavoritesOnly, setShowFavoritesOnly,
  sharedDaysToExit, setSharedDaysToExit,
  daysToExitMode, setDaysToExitMode,
  daysToExitPercent, setDaysToExitPercent,
  defaultDays,
  filters,
}) {
  const [selectedScenarios, setSelectedScenariosRaw] = useState(() => getUserSpreadScenarios());
  const setSelectedScenarios = useCallback((next) => {
    setSelectedScenariosRaw((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      if (resolved instanceof Set) saveUserSpreadScenarios(resolved);
      return resolved;
    });
  }, []);

  const [displayMode, setDisplayMode] = useState('returnOnRisk'); // | 'valueGain'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [debouncedDays, setDebouncedDays] = useState(sharedDaysToExit);

  // Keep the local debounced value in step when the parent seeds it after mount.
  React.useEffect(() => {
    if (sharedDaysToExit != null && debouncedDays == null) setDebouncedDays(sharedDaysToExit);
  }, [sharedDaysToExit, debouncedDays]);

  const handleDaysChange = useCallback((val) => {
    setDaysToExitMode('fixed');
    setDaysToExitPercent(null);
    setSharedDaysToExit(val);
    clearTimeout(handleDaysChange._timer);
    handleDaysChange._timer = setTimeout(() => setDebouncedDays(val), 80);
  }, [setSharedDaysToExit, setDaysToExitMode, setDaysToExitPercent]);

  const handlePercentChange = useCallback((pct) => {
    setDaysToExitMode('percent');
    setDaysToExitPercent(pct);
    setSharedDaysToExit(null);
    setDebouncedDays(null);
  }, [setDaysToExitMode, setDaysToExitPercent, setSharedDaysToExit]);

  const handleReset = useCallback(() => {
    setDaysToExitMode('per-contract');
    setSharedDaysToExit(null);
    setDaysToExitPercent(null);
    setDebouncedDays(null);
    clearUserSpreadScenarios();
    setSelectedScenariosRaw(new Set(DEFAULT_SPREAD_SCENARIOS));
  }, [setDaysToExitMode, setSharedDaysToExit, setDaysToExitPercent]);

  const scenarioArray = useMemo(
    () => Array.from(selectedScenarios).sort((a, b) => a - b),
    [selectedScenarios]
  );

  const filteredData = useMemo(() => {
    if (!Array.isArray(results)) return [];
    const { tickers = [], expirations = [], spreadTypes = [] } = filters || {};
    return results.filter((row) => {
      if (!row || typeof row !== 'object') return false;
      const key = rowKey(row);
      return (
        (tickers.length === 0 || tickers.includes(row['Ticker']))
        && (expirations.length === 0 || expirations.includes(row['Expiration']))
        && (spreadTypes.length === 0 || spreadTypes.includes(row['Spread Type']))
        && !removedRows.has(key)
        && (!showFavoritesOnly || favoritedRows.has(key))
      );
    });
  }, [results, filters, removedRows, showFavoritesOnly, favoritedRows]);

  // Nearest expiration bounds the Days-to-Exit slider: holding past it is
  // meaningless for the shortest-dated spread on screen.
  const maxDaysData = useMemo(() => {
    const source = filteredData.length > 0 ? filteredData : (results || []);
    if (source.length === 0) return { days: 365, date: null };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exps = source
      .map((r) => r['Expiration'])
      .filter((e) => e && e !== '-')
      .map((e) => {
        const d = new Date(e);
        d.setHours(0, 0, 0, 0);
        return { days: Math.ceil((d - today) / 86400000), date: e };
      })
      .filter((x) => x.days > 0)
      .sort((a, b) => a.days - b.days);
    return exps.length ? { days: exps[0].days, date: exps[0].date } : { days: 365, date: null };
  }, [filteredData, results]);

  const heatmapData = useMemo(() => {
    const isPercent = daysToExitMode === 'percent' && daysToExitPercent != null;
    return filteredData.map((row) => {
      const dte = parseFloat(row['Days Until Expiration']);
      const rowDays = isPercent
        ? Math.max(1, Math.round(dte * daysToExitPercent))
        : debouncedDays;
      const effectiveDays = rowDays != null
        ? rowDays
        : (parseFloat(row['Days to Gain']) || Math.max(1, Math.round(dte * 0.35)));
      const T = Math.max((dte - effectiveDays) / 365, 0.0001);

      return {
        ...row,
        _heatmap: computeSpreadHeatmapRow(
          row,
          isPercent ? null : debouncedDays,
          scenarioArray,
          isPercent ? daysToExitPercent : null
        ),
        _daysToExit: effectiveDays,
        _exitBreakeven: row._legs
          ? findSpreadBreakeven(row._legs, parseFloat(row['Net Entry']), T)
          : null,
      };
    });
  }, [filteredData, debouncedDays, scenarioArray, daysToExitMode, daysToExitPercent]);

  // Ticker, then nearest expiration, then strike — same spine the screener
  // heatmap uses when the user hasn't picked a sort.
  const defaultSort = (a, b) => {
    const t = String(a['Ticker'] || '').localeCompare(String(b['Ticker'] || ''));
    if (t !== 0) return t;
    const e = String(a['Expiration'] || '').localeCompare(String(b['Expiration'] || ''));
    if (e !== 0) return e;
    return (parseFloat(a['Long Strike']) || 0) - (parseFloat(b['Long Strike']) || 0);
  };

  const sortedData = useMemo(() => {
    const sortable = [...heatmapData];
    sortable.sort((a, b) => {
      const aFav = favoritedRows.has(rowKey(a)) ? 0 : 1;
      const bFav = favoritedRows.has(rowKey(b)) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      if (!sortConfig.key) return defaultSort(a, b);

      let aVal;
      let bVal;
      const scenarioMatch = sortConfig.key.match(/^scenario_(-?\d+(?:\.\d+)?)$/);
      if (scenarioMatch) {
        const pct = parseFloat(scenarioMatch[1]);
        const metric = displayMode === 'valueGain' ? 'valueGain' : 'returnOnRisk';
        aVal = a._heatmap?.[pct]?.[metric];
        bVal = b._heatmap?.[pct]?.[metric];
      } else if (sortConfig.key === 'Strikes') {
        aVal = parseFloat(a['Long Strike']);
        bVal = parseFloat(b['Long Strike']);
      } else if (sortConfig.key === 'DaysToExit') {
        aVal = a._daysToExit;
        bVal = b._daysToExit;
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (sortConfig.key === 'Expiration') {
        const cmp = String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      }
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      const cmp = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase());
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
    return sortable;
  }, [heatmapData, sortConfig, favoritedRows, displayMode]);

  const handleSort = (column) => {
    setSortConfig((prev) => (
      prev.key === column
        ? { key: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key: column, direction: 'asc' }
    ));
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return <FaSort className="inline ml-1 opacity-50" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="inline ml-1" />
      : <FaSortDown className="inline ml-1" />;
  };

  const toggleFavorite = (row) => {
    const key = rowKey(row);
    setFavoritedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const removeRow = (row) => {
    const key = rowKey(row);
    setRemovedRows((prev) => new Set([...prev, key]));
    setFavoritedRows((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const formatEssential = (row, colKey) => {
    switch (colKey) {
      case 'Strikes':
        return formatStrikePair(row['Long Strike'], row['Short Strike']);
      case 'Net Entry':
        return `${formatCurrency(row['Net Entry'])} ${row['Entry Type'] === 'Credit' ? 'Cr' : 'Dr'}`;
      case 'Max Profit':
      case 'Max Loss':
      case 'Breakeven':
      case 'Underlying Price':
        return formatCurrency(row[colKey]);
      case 'DaysToExit':
        return row._daysToExit ?? '-';
      case 'Spread Type':
        // "Call Debit Spread" -> "Call Debit": the word Spread is redundant here.
        return String(row['Spread Type'] || '').replace(' Spread', '');
      default:
        return row[colKey] ?? '-';
    }
  };

  const csvData = useMemo(() => sortedData.map((row) => {
    const out = {};
    ESSENTIAL_COLUMNS.forEach((col) => {
      const label = col.key === 'DaysToExit' ? 'Days to Exit' : col.label.replace('\n', ' ');
      out[label] = col.key === 'Strikes'
        ? formatStrikePair(row['Long Strike'], row['Short Strike'])
        : (col.key === 'DaysToExit' ? row._daysToExit : row[col.key]);
    });
    scenarioArray.forEach((pct) => {
      const cell = row._heatmap?.[pct];
      const value = displayMode === 'valueGain' ? cell?.valueGain : cell?.returnOnRisk;
      out[formatScenarioLabel(pct)] = value != null ? Number(value).toFixed(2) : '';
    });
    return out;
  }), [sortedData, scenarioArray, displayMode]);

  const csvHeaders = useMemo(() => {
    const headers = ESSENTIAL_COLUMNS.map((col) => {
      const label = col.key === 'DaysToExit' ? 'Days to Exit' : col.label.replace('\n', ' ');
      return { label, key: label };
    });
    scenarioArray.forEach((pct) => {
      const label = formatScenarioLabel(pct);
      headers.push({ label, key: label });
    });
    return headers;
  }, [scenarioArray]);

  if (!Array.isArray(results) || results.length === 0) return null;

  return (
    <div className="mt-6">
      {/* Wrapper anchor: the panel is a shared component and stays tour-agnostic. */}
      <div data-tour="spread-heatmap-settings">
        <HeatmapSettingsPanel
        maxDays={maxDaysData.days}
        maxExpirationDate={maxDaysData.date}
        daysToExit={sharedDaysToExit ?? defaultDays}
        onDaysChange={handleDaysChange}
        selectedScenarios={selectedScenarios}
        onScenariosChange={setSelectedScenarios}
        scenarioOptions={SPREAD_SCENARIO_OPTIONS}
        defaultScenarioSet={DEFAULT_SPREAD_SCENARIOS}
        contractCount={sortedData.length}
        defaultExpanded
        defaultDays={defaultDays}
        onReset={handleReset}
        isOverridden={sharedDaysToExit !== null}
        daysToExitMode={daysToExitMode}
        onModeChange={setDaysToExitMode}
        daysToExitPercent={daysToExitPercent}
        onPercentChange={handlePercentChange}
      />
      </div>

      {/* Control bar */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2" data-tour="spread-heatmap-controls">
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={() => setDisplayMode('returnOnRisk')}
            className={`tab-button ${displayMode === 'returnOnRisk' ? 'tab-selected' : 'tab-unselected'}`}
            title="Projected profit or loss as a percentage of the capital at risk"
          >
            Return on Risk %
          </button>
          <button
            type="button"
            onClick={() => setDisplayMode('valueGain')}
            className={`tab-button ${displayMode === 'valueGain' ? 'tab-selected' : 'tab-unselected'}`}
            title="Percentage change in the spread's own value"
          >
            Spread Value Gain %
          </button>
          {sortConfig.key && (
            <button
              type="button"
              onClick={() => setSortConfig({ key: null, direction: 'asc' })}
              className="flex items-center gap-1 font-medium text-sm py-1 px-3 rounded transition-all duration-200 border bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-[#218aff]/10 hover:text-[#218aff] hover:border-[#218aff]"
              title="Reset to default sort: Ticker → Expiration → Strike"
            >
              ↺ Default Sort
            </button>
          )}
        </div>

        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={() => setShowFavoritesOnly((p) => !p)}
            disabled={favoritedRows.size === 0 && !showFavoritesOnly}
            className={`flex items-center gap-1.5 font-medium text-sm py-1 px-3 rounded transition-all duration-200 border ${
              showFavoritesOnly
                ? 'bg-yellow-400/20 text-yellow-400 border-yellow-400'
                : favoritedRows.size === 0
                  ? 'bg-[#1f2937] text-[#555] border-[#333] cursor-not-allowed'
                  : 'bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-yellow-400/10 hover:text-yellow-400 hover:border-yellow-400'
            }`}
          >
            <Star className="w-3.5 h-3.5" fill={showFavoritesOnly || favoritedRows.size > 0 ? 'currentColor' : 'none'} />
            Favorites{favoritedRows.size > 0 ? ` (${favoritedRows.size})` : ''}
          </button>
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename="spread_simulator_optionsLeveling.csv"
            className="btn-export-primary"
          >
            Export CSV
          </CSVLink>
        </div>
      </div>

      {/* Heatmap table */}
      <div className="table-container" data-tour="spread-heatmap-table">
        <div className="table-scroll-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell-sticky table-header-cell-sticky-0" />
                {ESSENTIAL_COLUMNS.map((col) => {
                  const label = col.key === 'DaysToExit' ? 'Days to Exit' : col.label;
                  const tip = HEADER_TOOLTIPS[col.key] || HEADER_TOOLTIPS[label];
                  return (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      data-tour-col={tourColKey(col.key)}
                      className={col.stickyIndex
                        ? `table-header-cell-sticky table-header-cell-sticky-${col.stickyIndex}`
                        : 'table-header-cell'}
                      style={{
                        maxWidth: col.width,
                        ...(col.key === 'DaysToExit' ? { borderRight: '2px solid #374151' } : {}),
                      }}
                      {...(tip ? {
                        'data-tooltip-id': 'spread-header-tooltip',
                        'data-tooltip-content': tip,
                      } : {})}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <span className="text-center whitespace-pre-line">{col.label}</span>
                        <span className="mt-1">{getSortIcon(col.key)}</span>
                      </div>
                    </th>
                  );
                })}
                {scenarioArray.map((pct) => (
                  <th
                    key={pct}
                    onClick={() => handleSort(`scenario_${pct}`)}
                    data-tour-scenario={pct}
                    className="table-header-cell"
                    style={{ minWidth: '62px', maxWidth: '78px' }}
                    data-tooltip-id="spread-header-tooltip"
                    data-tooltip-content={pct === 0
                      ? 'Time decay only — no movement in the underlying'
                      : `Projected result if the underlying ${pct > 0 ? 'rises' : 'drops'} ${Math.abs(pct)}%`}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-center whitespace-nowrap">{formatScenarioLabel(pct)}</span>
                      <span className="mt-1">{getSortIcon(`scenario_${pct}`)}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sortedData.map((row, idx) => {
                const key = rowKey(row);
                const isFavorited = favoritedRows.has(key);
                return (
                  <tr
                    key={key}
                    className={`${idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}${isFavorited ? ' table-row-favorited' : ''}`}
                  >
                    <td className="table-cell-sticky table-cell-sticky-0" style={{ padding: '2px' }} data-tour="spread-row-actions">
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => toggleFavorite(row)}
                          className="w-4 h-4 rounded-full border border-gray-600 hover:border-yellow-400 hover:bg-yellow-400/20 flex items-center justify-center transition-all duration-200 group"
                          title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Star
                            className={`w-3 h-3 transition-colors duration-200 ${isFavorited ? 'text-yellow-400' : 'text-gray-400 group-hover:text-yellow-400'}`}
                            fill={isFavorited ? 'currentColor' : 'none'}
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRow(row)}
                          className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#ef4444] hover:bg-[#ef4444]/20 flex items-center justify-center transition-all duration-200 group"
                          title="Remove from results"
                        >
                          <MinusCircle className="w-3 h-3 text-gray-400 group-hover:text-[#ef4444]" />
                        </button>
                      </div>
                    </td>

                    {ESSENTIAL_COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        data-tour-col={tourColKey(col.key)}
                        className={`${col.stickyIndex ? `table-cell-sticky table-cell-sticky-${col.stickyIndex}` : 'table-cell'} table-cell-number`}
                        style={{
                          maxWidth: col.width,
                          ...(col.key === 'DaysToExit' ? { borderRight: '2px solid #374151' } : {}),
                          ...(col.key === 'Max Profit' ? { color: '#1DB954' } : {}),
                          ...(col.key === 'Max Loss' ? { color: '#ef4444' } : {}),
                        }}
                      >
                        {formatEssential(row, col.key)}
                      </td>
                    ))}

                    {scenarioArray.map((pct) => {
                      const cell = row._heatmap?.[pct];
                      const raw = displayMode === 'valueGain' ? cell?.valueGain : cell?.returnOnRisk;
                      const style = getSpreadCellColor(raw, displayMode);
                      const fmtD = (v) => (v != null && !isNaN(v) ? `$${Number(v).toFixed(2)}` : '--');
                      const be = row._exitBreakeven;
                      const tooltipHtml =
                        '<div style="display:flex;flex-direction:column;gap:2px">'
                        + `<div style="display:flex;justify-content:space-between;gap:12px"><span style="color:#9ca3af">Spread:</span><span>${fmtD(row['Net Entry'])} → ${fmtD(cell?.simulatedValue)}</span></div>`
                        + `<div style="display:flex;justify-content:space-between;gap:12px"><span style="color:#9ca3af">Underlying:</span><span>${fmtD(row['Underlying Price'])} → ${fmtD(cell?.simulatedStock)}</span></div>`
                        + `<div style="display:flex;justify-content:space-between;gap:12px"><span style="color:#9ca3af">P&L:</span><span>${cell?.pnlDollars != null ? `$${Number(cell.pnlDollars).toFixed(0)}` : '--'}</span></div>`
                        + (be != null ? `<div style="display:flex;justify-content:space-between;gap:12px;border-top:1px solid #374151;padding-top:2px;margin-top:1px"><span style="color:#9ca3af">Breakeven:</span><span>${fmtD(be)}</span></div>` : '')
                        + '</div>';

                      return (
                        <td
                          key={pct}
                          data-tour-scenario={pct}
                          className="table-cell text-center font-medium whitespace-nowrap"
                          style={{ ...style, minWidth: '62px', cursor: 'help' }}
                          data-tooltip-id="spread-cell-tooltip"
                          data-tooltip-html={tooltipHtml}
                        >
                          {formatSpreadCell(raw, displayMode)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-between items-center text-sm text-gray-400 mt-3">
        <div>Showing {sortedData.length} of {results.length} spreads</div>
        <div className="text-xs">
          {displayMode === 'returnOnRisk'
            ? 'Cells show projected P&L as a % of max loss (capital at risk)'
            : "Cells show the projected % change in the spread's own value"}
        </div>
      </div>

      <ReactTooltip
        id="spread-header-tooltip"
        place="top"
        delayShow={300}
        style={{
          backgroundColor: '#111827', color: '#fff', borderRadius: 8, padding: '8px 12px',
          fontSize: 13, maxWidth: 280, lineHeight: 1.4, zIndex: 1200,
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)', border: '1px solid #374151',
        }}
      />
      <ReactTooltip
        id="spread-cell-tooltip"
        place="top"
        delayShow={200}
        style={{
          backgroundColor: '#111827', color: '#fff', borderRadius: 8, padding: '8px 12px',
          fontSize: 12, maxWidth: 260, lineHeight: 1.6, zIndex: 1200,
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)', border: '1px solid #374151',
        }}
      />
    </div>
  );
}

export default SpreadHeatmapView;
